// A bounded, display-only projection of what a declared source's tool returned for the selected saved version.
// It draws values as they came (scalars only), never invents a column, count or label, and bounds what it shows.
// Shapes: a list of records -> a table; a record -> its scalar facts; a scalar -> text; nothing -> empty.
export const PREVIEW_MAX_ROWS = 10;
export const PREVIEW_MAX_COLUMNS = 8;
const MAX_CELL = 120;

export type PreviewView =
  | { kind: 'table'; columns: string[]; rows: string[][]; more: boolean }
  | { kind: 'facts'; entries: [string, string][] }
  | { kind: 'text'; text: string }
  /** An answer with no declared shape: its bounded JSON, plainly. */
  | { kind: 'json'; text: string }
  | { kind: 'empty' };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const scalar = (value: unknown): string | null =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
    ? String(value)
    : null;
const clip = (text: string) => (text.length > MAX_CELL ? `${text.slice(0, MAX_CELL - 1)}…` : text);

/** The first list of records in the answer: the answer itself, or the first such value of a record. */
function recordList(data: unknown): Record<string, unknown>[] | null {
  const listed = (value: unknown) =>
    Array.isArray(value) && value.length > 0 && value.every(isRecord)
      ? (value as Record<string, unknown>[])
      : null;
  if (Array.isArray(data)) return listed(data);
  if (isRecord(data))
    for (const value of Object.values(data)) {
      const found = listed(value);
      if (found) return found;
    }
  return null;
}

const MAX_JSON = 4000;
const plain = (data: unknown): PreviewView => {
  const empty =
    data === null ||
    data === undefined ||
    (Array.isArray(data) && data.length === 0) ||
    (isRecord(data) && Object.keys(data).length === 0);
  if (empty) return { kind: 'empty' };
  const text = JSON.stringify(data, null, 2) ?? '';
  return text
    ? { kind: 'json', text: text.length > MAX_JSON ? `${text.slice(0, MAX_JSON - 1)}…` : text }
    : { kind: 'empty' };
};

export function projectPreview(data: unknown): PreviewView {
  const records = recordList(data);
  if (records) {
    const columns: string[] = [];
    for (const record of records.slice(0, PREVIEW_MAX_ROWS))
      for (const [key, value] of Object.entries(record))
        if (
          scalar(value) !== null &&
          !columns.includes(key) &&
          columns.length < PREVIEW_MAX_COLUMNS
        )
          columns.push(key);
    if (columns.length === 0) return plain(data);
    return {
      kind: 'table',
      columns,
      rows: records
        .slice(0, PREVIEW_MAX_ROWS)
        .map((record) => columns.map((key) => clip(scalar(record[key]) ?? ''))),
      more: records.length > PREVIEW_MAX_ROWS,
    };
  }
  if (isRecord(data)) {
    const entries = Object.entries(data)
      .map(([key, value]): [string, string | null] => [key, scalar(value)])
      .filter((entry): entry is [string, string] => entry[1] !== null)
      .slice(0, PREVIEW_MAX_COLUMNS * 2)
      .map(([key, value]): [string, string] => [key, clip(value)]);
    return entries.length ? { kind: 'facts', entries } : plain(data);
  }
  const text = scalar(data);
  return text ? { kind: 'text', text: clip(text) } : plain(data);
}
